import { createElement, type CSSProperties, type ElementType, useMemo } from 'react';
import sanitizeHtml from 'sanitize-html';
import { cn } from '@/utils/tailwind.util';
import { normalizeLegacySpanLatex } from '../utils/normalizeLegacySpanLatex';
import { parseMathContent } from '../utils/parseMathContent';
import { renderLatexToMathML } from '../utils/renderLatexToMathML';
type SupportedTag = 'span' | 'div' | 'p' | 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6';
interface StaticRichTextRendererProps {
  text: string;
  /**
   * 래퍼 엘리먼트. 기본값 `span` (인라인). 입력에 `<p>`, `<h1>` 등 블록
   * 노드가 포함될 가능성이 있다면(Tiptap 에디터 출력) `div`로 지정한다.
   */
  as?: SupportedTag;
  className?: string;
  style?: CSSProperties;
}

// Tiptap StarterKit + Highlight 확장이 만들어내는 블록/마크 노드를 모두
// 통과시킨다. 그 외 태그는 escape되어 화면에 가시 텍스트로 보인다.
const TIPTAP_TAGS = [
// 블록
'p', 'br', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'ul', 'ol', 'li', 'blockquote', 'hr', 'pre',
// 마크
'strong', 'em', 's', 'code', 'u', 'mark'];

// `<mark>` (Highlight 확장)는 multicolor 옵션이 켜져 있어 색상이 `data-color`
// 와 inline `style`(background-color/color) 둘 다로 저장된다. 색상 값은
// 단어/16진수/rgb()/percent만 허용해 javascript: 등의 위험 페이로드를 차단.
const SAFE_COLOR = /^[a-zA-Z0-9#(),.\s%]+$/;
const SANITIZE_OPTIONS_TEXT: sanitizeHtml.IOptions = {
  allowedTags: TIPTAP_TAGS,
  allowedAttributes: {
    mark: ['data-color', 'style']
  },
  allowedStyles: {
    mark: {
      'background-color': [SAFE_COLOR],
      color: [SAFE_COLOR]
    }
  },
  disallowedTagsMode: 'escape'
};
const SANITIZE_OPTIONS_PREPARED: sanitizeHtml.IOptions = {
  ...SANITIZE_OPTIONS_TEXT,
  allowedTags: [...TIPTAP_TAGS, 'math-inline'],
  allowedAttributes: {
    ...SANITIZE_OPTIONS_TEXT.allowedAttributes,
    'math-inline': ['data-latex', 'data-display']
  }
};
const MATH_INLINE_TAG = /<math-inline\b([^>]*?)(?:\s*\/>|>\s*<\/math-inline>|>)/g;
const ATTR_LATEX = /data-latex="([^"]*)"/;
const ATTR_DISPLAY = /data-display="true"/;
const decodeHtmlEntities = (value: string): string => value.replace(/&lt;/g, '<').replace(/&gt;/g, '>').replace(/&quot;/g, '"').replace(/&#039;/g, "'").replace(/&amp;/g, '&');

// MathML 출력은 가독성을 위해 source-level 줄바꿈과 들여쓰기를 포함하는데,
// 컨테이너가 `whitespace-pre-wrap`이면 그 공백이 가시 줄바꿈으로 새어 나온다.
// 각 수식을 white-space: normal 컨테이너로 격리한다. display 수식
// (`$$...$$`)은 Temml의 block-level 출력이 의도한 독립 줄/가운데 정렬이
// 유지되도록 block 컨테이너로 감싼다.
const wrapMathHtml = (html: string, display: boolean): string => display ? `<span style="display:block;text-align:center;white-space:normal">${html}</span>` : `<span style="display:inline-block;white-space:normal;vertical-align:middle">${html}</span>`;
const renderMathSegments = (text: string): string => parseMathContent(text).map(segment => {
  if (segment.type === 'math') {
    return wrapMathHtml(renderLatexToMathML(decodeHtmlEntities(segment.content), segment.display), segment.display);
  }
  return segment.content;
}).join('');

// 사전 처리된 HTML(`<math-inline>` 포함)을 렌더링한다.
// `<math-inline>` 사이에 끼인 텍스트(예: 저장-재불러오기 과정에서 섞여 들어온
// raw `$...$`)도 함께 파싱해 부분적으로 수식이 누락되지 않게 한다.
const renderPreparedHTML = (html: string): string => {
  const safe = sanitizeHtml(html, SANITIZE_OPTIONS_PREPARED);
  // split의 캡처 그룹 덕분에 짝수 인덱스는 주변 마크업, 홀수 인덱스는
  // math-inline 속성 문자열이 된다.
  const parts = safe.split(MATH_INLINE_TAG);
  return parts.map((part, idx) => {
    if (idx % 2 === 1) {
      const latexRaw = part.match(ATTR_LATEX)?.[1] ?? '';
      const display = ATTR_DISPLAY.test(part);
      return wrapMathHtml(renderLatexToMathML(decodeHtmlEntities(latexRaw), display), display);
    }
    return renderMathSegments(part);
  }).join('');
};
const renderRawText = (text: string): string => {
  const sanitized = sanitizeHtml(text, SANITIZE_OPTIONS_TEXT);
  return renderMathSegments(sanitized);
};

/**
 * ZEP rich text 콘텐츠 전용 정적 read-only 렌더러.
 *
 * ProseMirror EditorView 인스턴스 없이 sanitize된 HTML 문자열로 1회 렌더링한다.
 * 리스트가 많은 화면(예: AI 퀴즈 결과 카드 × 객관식 보기)에서 Tiptap
 * 에디터 인스턴스가 O(카드 수 × 항목 수)로 증가하는 문제를 방지하기 위해 도입.
 *
 * 입력 형식: raw 텍스트(`$...$` / `$$...$$` / ZWSP 래핑) 또는 Tiptap 저장
 * HTML(`<p>`, `<mark>`, `<math-inline>` 등 포함) 모두 허용. Tiptap 출력처럼
 * `<p>` 등 블록 노드가 포함될 수 있는 입력은 `as="div"`로 지정해야 한다.
 *
 * 지원하는 마크/노드는 StarterKit + Highlight + MathInline 범위. 그 외 태그는
 * 엔티티로 escape되어 화면에 가시 텍스트로 보인다.
 */
export const StaticRichTextRenderer = ({
  text,
  as = 'span',
  className,
  style
}: StaticRichTextRendererProps) => {
  const html = useMemo(() => {
    const normalized = normalizeLegacySpanLatex(text);
    return normalized.includes('<math-inline') ? renderPreparedHTML(normalized) : renderRawText(normalized);
  }, [text]);
  return createElement(as as ElementType, {
    className: cn('tiptap max-w-full whitespace-pre-wrap [overflow-wrap:anywhere]', as === 'span' && 'inline-block', className),
    style,
    dangerouslySetInnerHTML: {
      __html: html
    }
  });
};